/**
 * Общие части гостевого меню (design-guest v7 §10/§11/§12): степпер
 * «− N +», ярлыки аллергенов, эвристика часов/доступности категории.
 *
 * Гостевое меню — route-based поток (как в макете v7):
 *   §10 /menu            — плитки категорий (tile-grid);
 *   §11 /menu/c/:code    — row-cards позиций категории;
 *   §12 /menu/order      — корзина и подтверждение.
 * Корзина — sticky-CTA «Перейти к заказу · N · ₽» (намеренно НЕ модалка).
 */
import type { JSX } from 'react';

/** Русские ярлыки тегов аллергенов/диет (совпадают с менеджерским §9.3). */
const ALLERGEN_LABELS: Readonly<Record<string, string>> = {
  veg: 'вег',
  vegan: 'веган',
  gluten: 'глютен',
  lactose: 'лактоза',
  nuts: 'орехи',
  spicy: 'остро',
  egg: 'яйца',
  eggs: 'яйца',
  fish: 'рыба',
  seafood: 'морепродукты',
  honey: 'мёд',
  soy: 'соя',
};

/** Аллергены, которые подсвечиваем красным (потенциально опасные). */
const ALLERGEN_WARN = new Set([
  'gluten',
  'lactose',
  'nuts',
  'egg',
  'eggs',
  'fish',
  'seafood',
  'soy',
]);

export function allergenLabel(tag: string): string {
  return ALLERGEN_LABELS[tag] ?? tag;
}

/** true → аллерген совпал с предпочтениями гостя (показываем красный warning). */
export function isAllergenWarn(tag: string, guestAllergens: readonly string[]): boolean {
  return guestAllergens.includes(tag) || ALLERGEN_WARN.has(tag);
}

/**
 * Часы/доступность категории по её коду (design §10: «07:00 — 11:00 ·
 * доступно», недоступные — opacity 0.7). Реальных часов в каталоге нет —
 * выводим из семантики кода (завтрак/обед/ужин/бар/снеки/детское). Если код
 * не распознан — возвращаем «доступно» и считаем категорию открытой.
 */
export interface CategoryAvailability {
  /** Подпись часов/режима под названием плитки. */
  hours: string;
  /** false → сейчас закрыта (плитка тусклее, но кликабельна). */
  open: boolean;
}

const HOURS_BY_KIND: ReadonlyArray<{ match: RegExp; from: number; to: number; label: string }> = [
  { match: /(breakfast|завтрак|morning)/i, from: 7, to: 11, label: '07:00 — 11:00' },
  { match: /(lunch|обед|dinner_lunch)/i, from: 12, to: 16, label: '12:00 — 16:00' },
  { match: /(dinner|ужин|supper)/i, from: 17, to: 22, label: '17:00 — 22:00' },
  { match: /(bar|бар|drink|cocktail|wine|minibar)/i, from: 10, to: 26, label: '10:00 — 02:00' },
  { match: /(kid|child|детск)/i, from: 11, to: 21, label: '11:00 — 21:00' },
];

export function categoryAvailability(code: string, name: string): CategoryAvailability {
  const probe = `${code} ${name}`;
  const now = new Date().getHours();
  for (const h of HOURS_BY_KIND) {
    if (h.match.test(probe)) {
      // to>24 — окно через полночь (бар 10:00—02:00).
      const open = h.to > 24 ? now >= h.from || now < h.to - 24 : now >= h.from && now < h.to;
      return { hours: open ? `${h.label} · доступно` : h.label, open };
    }
  }
  if (/(snack|снек|24)/i.test(probe)) return { hours: '24 / 7', open: true };
  return { hours: 'доступно', open: true };
}

/** Степпер количества: − N + (или кнопка «В заказ» при qty=0). */
export function QtyStepper({
  qty,
  onAdd,
  onInc,
  onDec,
  testid,
}: {
  qty: number;
  onAdd: () => void;
  onInc: () => void;
  onDec: () => void;
  testid: string;
}): JSX.Element {
  if (qty === 0) {
    return (
      <button type="button" className="fnb-add" data-testid={`${testid}-add`} onClick={onAdd}>
        + В заказ
      </button>
    );
  }
  return (
    <span className="fnb-stepper" role="group" aria-label="Количество">
      <button
        type="button"
        className="fnb-stepper__btn"
        aria-label="Убрать одну"
        data-testid={`${testid}-dec`}
        onClick={onDec}
      >
        −
      </button>
      <span className="fnb-stepper__qty" data-testid={`${testid}-qty`}>
        {qty}
      </span>
      <button
        type="button"
        className="fnb-stepper__btn"
        aria-label="Добавить одну"
        data-testid={`${testid}-inc`}
        onClick={onInc}
      >
        +
      </button>
    </span>
  );
}

/**
 * Семейство брендовых иконок категории (Артём 2026-06-18: «в нашей
 * стилистике», вместо эмодзи). Line-icon, stroke=currentColor 1.75, как
 * иконки навигации — единый визуальный язык. Видны и у гостя (плитки §10),
 * и у менеджера (placeholder обложки §9.2).
 */
export type CategoryIconKind = 'kitchen' | 'drinks' | 'hookah' | 'dessert' | 'kids';

const SVG_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

/** Маппинг code/name → вид брендовой иконки категории. */
export function categoryIconKind(code: string, name: string): CategoryIconKind {
  const probe = `${code} ${name}`.toLowerCase();
  if (/(hookah|кальян|shisha|кальянная)/.test(probe)) return 'hookah';
  if (/(bar|бар|drink|напит|cocktail|коктейл|wine|вино|minibar|кофе|coffee|чай|tea)/.test(probe)) {
    return 'drinks';
  }
  if (/(dessert|десерт|cake|торт|выпеч|сладк|pastry)/.test(probe)) return 'dessert';
  if (/(kid|child|детск)/.test(probe)) return 'kids';
  // kitchen|кухня|завтрак|breakfast|обед|ужин|горяч|меню — кухня (fallback).
  return 'kitchen';
}

/** Брендовая SVG-иконка категории по её виду. */
export function CategoryIcon({
  kind,
  className,
}: {
  kind: CategoryIconKind;
  className?: string;
}): JSX.Element {
  switch (kind) {
    case 'drinks':
      // Бокал/чашка с напитком.
      return (
        <svg {...SVG_PROPS} className={className}>
          <path d="M6 4h12l-1.2 8.5a5 5 0 0 1-9.6 0L6 4Z" />
          <path d="M12 17v3" />
          <path d="M8.5 20h7" />
          <path d="M7 8h10" />
        </svg>
      );
    case 'hookah':
      // Кальян: колба, шахта, шланг.
      return (
        <svg {...SVG_PROPS} className={className}>
          <path d="M12 3v8" />
          <path d="M10.5 3h3" />
          <path d="M9 11h6a4 4 0 0 1 4 4v0a4 4 0 0 1-4 4h-2" />
          <path d="M9 11a4 4 0 0 0-4 4v3" />
          <path d="M3.5 18h3" />
          <path d="M12 5.5c1.5 0 1.5 1.5 3 1.5" />
        </svg>
      );
    case 'dessert':
      // Кекс/десерт.
      return (
        <svg {...SVG_PROPS} className={className}>
          <path d="M6 11h12l-1.3 8.2a1 1 0 0 1-1 .8H8.3a1 1 0 0 1-1-.8L6 11Z" />
          <path d="M5 11a3 3 0 0 1 1.6-2.6A3 3 0 0 1 12 6a3 3 0 0 1 5.4 2.4A3 3 0 0 1 19 11" />
          <path d="M12 4v1.5" />
        </svg>
      );
    case 'kids':
      // Детское: бутылочка/соска.
      return (
        <svg {...SVG_PROPS} className={className}>
          <path d="M9 8h6v9a3 3 0 0 1-3 3v0a3 3 0 0 1-3-3V8Z" />
          <path d="M10 8V6h4v2" />
          <path d="M11 4h2" />
          <path d="M9 12h6" />
        </svg>
      );
    case 'kitchen':
    default:
      // Кухня: поварской колпак.
      return (
        <svg {...SVG_PROPS} className={className}>
          <path d="M7 14h10v4a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-4Z" />
          <path d="M7 14a4 4 0 0 1-1-7.9A4 4 0 0 1 13 4a4 4 0 0 1 5 6.1A4 4 0 0 1 17 14" />
          <path d="M9 17h6" />
        </svg>
      );
  }
}

/** Брендовая иконка плитки категории по коду/имени (для гостя и менеджера). */
export function categoryIcon(code: string, name: string): JSX.Element {
  return <CategoryIcon kind={categoryIconKind(code, name)} />;
}
